import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { Store } from '@ngrx/store';

import { combineLatest } from 'rxjs';

import { RoleAccessDirective } from '../../../@core/directives/role-access.directive';
import { UserApiActions } from '../../../@store/user/user.actions';
import { selectFetchedUserRoles } from '../../../@store/user/user.selectors';
import { selectFetchedUser } from '../../../@store/user/user.selectors';
import { SafeAny } from '../../../types/safe-any';
import { ArchiveUserDialogComponent } from '../archive-user-dialog/archive-user-dialog.component';

@Component({
  selector: 'ciwc-user-details',
  standalone: true,
  imports: [
    CommonModule,
    MatRadioModule,
    MatButtonModule,
    MatIconModule,
    ReactiveFormsModule,
    RouterModule,
    RoleAccessDirective,
  ],
  templateUrl: './user-details.component.html',
  styleUrls: ['./user-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserDetailsComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  userForm = new FormGroup({
    roles: new FormControl(''),
  });

  roles: SafeAny[] = [];
  user: SafeAny = {};
  isStatusActive = false;
  userId: number | null = null;

  ngOnInit(): void {
    this.fetchUser();
    this.fetchUserRoles();
    this.initValues();
  }

  fetchUser(): void {
    this.userId = Number(this.route.snapshot.paramMap.get('id'));

    if (this.userId) {
      this.store.dispatch(UserApiActions.fetchUser({ userId: this.userId.toString() }));
    }
  }

  fetchUserRoles(): void {
    const payload = { identifier: 'USERS_ROLES' };
    this.store.dispatch(UserApiActions.fetchUserRoles({ payload }));
  }

  initValues(): void {
    combineLatest([this.store.select(selectFetchedUser), this.store.select(selectFetchedUserRoles)])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([user, roles]: [SafeAny, SafeAny]) => {
        if (!user || !roles) return;

        this.roles = roles;
        this.user = user;
        this.isStatusActive = user.status === 'ACTIVE';

        this.userForm.patchValue({
          roles: user.roles[0],
        });
      });
  }

  editDetails(): void {
    this.router.navigate([`/users/edit/${this.userId}`]);
  }

  changeUserStatus(): void {
    const dialogRef = this.dialog.open(ArchiveUserDialogComponent, {
      data: { user: this.user, isArchived: true },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const action = this.isStatusActive
          ? UserApiActions.archiveUser({ userId: this.user.id })
          : UserApiActions.unarchiveUser({ userId: this.user.id });

        this.store.dispatch(action);
      }
    });
  }

  resetPassword(): void {
    this.store.dispatch(UserApiActions.resetPassword({ userId: this.user.id }));
  }
}
